import React, { useId, useState } from 'react';
import { AlertCircle, ArrowLeft, ArrowRight, Check, Loader2, X } from 'lucide-react';
import { registerUser, loginUser, loginWithGoogle, resetPassword } from '../utils/storage';
import { UserProfile } from '../types';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: UserProfile, isNewSignup: boolean) => void;
  isFirebaseConfigured: boolean;
  onShowSubscriptionModal: () => void;
  initialAuthMode?: 'login' | 'signup' | 'forgot';
  /** When true, modal cannot be closed - used for mandatory auth */
  required?: boolean;
}

// A sign-in form (docs/DESIGN_SYSTEM.md): pill fields (ns-input) and, beside them, buttons of
// the default size across the sheet, where other dialogs take ns-btn-sm at the bottom right.

export function AuthModal({
  isOpen,
  onClose,
  onLoginSuccess,
  isFirebaseConfigured,
  onShowSubscriptionModal,
  initialAuthMode = 'login',
  required = false,
}: AuthModalProps) {
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgot'>(initialAuthMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [resetLink, setResetLink] = useState('');
  const [verificationLink, setVerificationLink] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const titleId = useId();

  React.useEffect(() => {
    if (isOpen) {
      setAuthMode(initialAuthMode);
      setAuthError('');
      setAuthSuccess('');
    }
  }, [isOpen, initialAuthMode]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsAuthenticating(true);
    try {
      if (authMode === 'signup') {
        if (!name.trim()) throw new Error('Name is required');
        const newUser = await registerUser(name, email, password);
        if (newUser.verificationLink) {
          setVerificationLink(newUser.verificationLink);
          setAuthSuccess('Account created! Please verify your email:');
          setIsAuthenticating(false);
          setTimeout(() => onLoginSuccess(newUser, true), 100);
        } else {
          onLoginSuccess(newUser, true);
        }
      } else {
        const loggedUser = await loginUser(email, password);
        onLoginSuccess(loggedUser, false);
      }
    } catch (err: any) {
      let msg = err.message || 'Authentication failed';
      if (msg.includes('auth/email-already-in-use')) msg = 'Email already registered.';
      if (msg.includes('auth/invalid-credential')) msg = 'Invalid email or password.';
      setAuthError(msg);
      setIsAuthenticating(false);
    }
  };

  const handleGoogleAuth = async () => {
    setAuthError('');
    setIsAuthenticating(true);
    try {
      if (isFirebaseConfigured) {
        const userProfile = await loginWithGoogle();
        onLoginSuccess(userProfile, false);
      } else {
        setAuthError('Firebase is not configured.');
        setIsAuthenticating(false);
      }
    } catch (err: any) {
      setAuthError(err.message);
      setIsAuthenticating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ns-modal-scrim" onClick={() => !required && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="ns-modal-sheet max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {!required && (
          <button
            onClick={onClose}
            className="ns-icon-btn absolute top-5 right-5"
            aria-label="Close"
          >
            <X />
          </button>
        )}

        {authMode === 'forgot' ? (
          <>
            <button
              onClick={() => {
                setAuthMode('login');
                setAuthError('');
                setAuthSuccess('');
              }}
              className="ns-btn ns-btn-xs ns-btn-quiet -ml-3 mb-3"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <h2 id={titleId} className="ns-t-dialog">Reset password</h2>
            <p className="text-sm text-ink-soft mt-1 mb-6">
              Enter your email and we'll send you a reset link.
            </p>
            <form
              onSubmit={async e => {
                e.preventDefault();
                setIsAuthenticating(true);
                setAuthError('');
                setAuthSuccess('');
                setResetLink('');
                try {
                  const result = await resetPassword(email);
                  if (result.resetLink) {
                    setResetLink(result.resetLink);
                    setAuthSuccess('Click the link below to reset your password:');
                  } else {
                    setAuthSuccess('Password reset email sent! Check your inbox.');
                  }
                } catch (err: any) {
                  setAuthError(err.message || 'Failed to send reset email');
                } finally {
                  setIsAuthenticating(false);
                }
              }}
              className="space-y-3"
            >
              <input
                type="email"
                placeholder="Email"
                autoComplete="email"
                className="ns-input"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setAuthError('');
                  setAuthSuccess('');
                  setResetLink('');
                }}
                required
              />
              {authError && (
                <div className="ns-note" data-tone="danger">
                  <AlertCircle size={14} />
                  <p>{authError}</p>
                </div>
              )}
              {authSuccess && (
                <div className="ns-note" data-tone="ok">
                  <Check size={14} />
                  <p>{authSuccess}</p>
                </div>
              )}
              {resetLink ? (
                <a
                  href={resetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ns-btn ns-btn-lavender w-full"
                >
                  Reset password <ArrowRight size={16} />
                </a>
              ) : (
                <button type="submit" disabled={isAuthenticating} className="ns-btn ns-btn-primary w-full">
                  {isAuthenticating ? <Loader2 className="animate-spin" size={16} /> : 'Send reset link'}
                </button>
              )}
            </form>
          </>
        ) : (
          <>
            <h2 id={titleId} className="ns-t-dialog text-center">
              {authMode === 'login' ? 'Welcome back' : 'Create account'}
            </h2>
            <p className="text-sm text-ink-soft text-center mt-1 mb-6">
              {authMode === 'login'
                ? 'Sign in to access your workspaces'
                : 'Start free — 3 nodes to begin'}
            </p>

            {/* Tab pill */}
            <div className="ns-seg flex w-full mb-5">
              <button
                onClick={() => {
                  setAuthMode('login');
                  setAuthError('');
                }}
                aria-pressed={authMode === 'login'}
                className="ns-seg-item flex-1 py-2 text-sm"
              >
                Sign in
              </button>
              <button
                onClick={() => {
                  setAuthMode('signup');
                  setAuthError('');
                }}
                aria-pressed={authMode === 'signup'}
                className="ns-seg-item flex-1 py-2 text-sm"
              >
                Sign up
              </button>
            </div>

            <form onSubmit={handleEmailAuth} className="space-y-3">
              {authMode === 'signup' && (
                <input
                  type="text"
                  placeholder="Name"
                  autoComplete="name"
                  className="ns-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                />
              )}
              <input
                type="email"
                placeholder="Email"
                autoComplete="email"
                className="ns-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password"
                autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                className="ns-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              {authMode === 'login' && (
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('forgot');
                    setAuthError('');
                  }}
                  className="ns-link text-xs"
                >
                  Forgot password?
                </button>
              )}
              {authError && (
                <div className="ns-note" data-tone="danger">
                  <AlertCircle size={14} />
                  <p>{authError}</p>
                </div>
              )}
              {authSuccess && (
                <div className="ns-note" data-tone="ok">
                  <Check size={14} />
                  <p>{authSuccess}</p>
                </div>
              )}
              {verificationLink ? (
                <a
                  href={verificationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ns-btn ns-btn-lavender w-full"
                >
                  <Check size={16} /> Verify email <ArrowRight size={16} />
                </a>
              ) : (
                <button type="submit" disabled={isAuthenticating} className="ns-btn ns-btn-primary w-full">
                  {isAuthenticating ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : authMode === 'login' ? (
                    'Sign in'
                  ) : (
                    'Create free account'
                  )}
                </button>
              )}
            </form>

            {isFirebaseConfigured && (
              <>
                <div className="my-5 flex items-center gap-3">
                  <span className="ns-divider flex-1" />
                  <span className="text-xs font-medium text-ink-faint">or</span>
                  <span className="ns-divider flex-1" />
                </div>
                <button
                  onClick={handleGoogleAuth}
                  disabled={isAuthenticating}
                  className="ns-btn ns-btn-ghost w-full"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />{/* ui-ok: the Google mark keeps Google's colours */}
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />{/* ui-ok: the Google mark keeps Google's colours */}
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />{/* ui-ok: the Google mark keeps Google's colours */}
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />{/* ui-ok: the Google mark keeps Google's colours */}
                  </svg>
                  Continue with Google
                </button>
              </>
            )}

            {authMode === 'signup' && (
              <p className="mt-5 text-xs text-center text-ink-soft">
                Want unlimited nodes & cloud sync?{' '}
                <button
                  onClick={() => {
                    onClose();
                    onShowSubscriptionModal();
                  }}
                  className="ns-link"
                >
                  Get Pro
                </button>
              </p>
            )}
          </>
        )}
        {required && (
          <p className="mt-5 text-xs text-center text-ink-faint">
            Sign in is required to use Noodle Studio
          </p>
        )}
      </div>
    </div>
  );
}
